import React, { useState } from 'react';
import { DiagnosticEntry } from '@/types';
import { BugReport, ExpandMore, ExpandLess } from '@mui/icons-material';

interface DiagnosticsPanelProps {
    entries: DiagnosticEntry[];
}

const CATEGORY_COLORS: Record<string, string> = {
    'user-action': '#60a5fa',
    'ai-prompt': '#c084fc',
    'ai-response': '#a78bfa',
    'ai-analysis': '#818cf8',
    'decision-resolution': '#f59e0b',
    'timeline-event': '#34d399',
    'npc-response': '#fb923c',
    'scoring': '#f472b6',
    'system': '#94a3b8',
};

const formatTime = (ts: string): string => {
    const d = new Date(ts);
    return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
};

const DiagnosticsPanel: React.FC<DiagnosticsPanelProps> = ({ entries }) => {
    const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
    const [filterCategory, setFilterCategory] = useState<string | null>(null);

    const filtered = filterCategory
        ? entries.filter(e => e.category === filterCategory)
        : entries;

    const categories = Array.from(new Set(entries.map(e => e.category)));

    return (
        <section className="bg-slate-800 border border-slate-700 rounded-xl p-4 flex flex-col">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <BugReport style={{ color: '#f472b6', fontSize: 16 }} />
                    <h3 className="text-[10px] font-bold uppercase tracking-widest text-pink-400">Diagnostics</h3>
                    <span className="text-[10px] text-slate-500">{entries.length} entries</span>
                </div>
            </div>

            {/* Category filter chips */}
            <div className="flex flex-wrap gap-1 mb-3">
                <button
                    onClick={() => setFilterCategory(null)}
                    className={`text-[9px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider transition-colors ${!filterCategory ? 'bg-slate-600 text-white' : 'bg-slate-900 text-slate-400 hover:text-white'}`}
                >
                    All
                </button>
                {categories.map(cat => (
                    <button
                        key={cat}
                        onClick={() => setFilterCategory(filterCategory === cat ? null : cat)}
                        className={`text-[9px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider transition-colors ${filterCategory === cat ? 'text-white' : 'text-slate-400 hover:text-white'}`}
                        style={filterCategory === cat ? { backgroundColor: CATEGORY_COLORS[cat] ?? '#94a3b8' } : { backgroundColor: '#0f172a' }}
                    >
                        {cat}
                    </button>
                ))}
            </div>

            {/* Entries — newest first */}
            <div className="max-h-96 overflow-y-auto space-y-1 pr-1">
                {filtered.length === 0 && (
                    <p className="text-[11px] text-slate-500 italic text-center py-4">No diagnostic entries yet.</p>
                )}
                {[...filtered].reverse().map((entry, idx) => {
                    const globalIdx = entries.length - 1 - idx;
                    const isExpanded = expandedIndex === globalIdx;
                    const hasDetail = !!entry.detail;

                    return (
                        <div key={idx} className="text-[11px]">
                            <button
                                onClick={() => hasDetail && setExpandedIndex(isExpanded ? null : globalIdx)}
                                className={`w-full text-left flex items-start gap-2 px-2 py-1 rounded transition-colors ${hasDetail ? 'hover:bg-slate-900/60 cursor-pointer' : 'cursor-default'}`}
                            >
                                <span
                                    className="size-1.5 rounded-full shrink-0 mt-1.5"
                                    style={{ backgroundColor: CATEGORY_COLORS[entry.category] ?? '#94a3b8' }}
                                />
                                <span className="font-mono text-slate-500 shrink-0">{formatTime(entry.timestamp)}</span>
                                <span className="text-slate-300 flex-1">{entry.summary}</span>
                                {hasDetail && (
                                    isExpanded
                                        ? <ExpandLess style={{ fontSize: 14 }} className="text-slate-500 shrink-0" />
                                        : <ExpandMore style={{ fontSize: 14 }} className="text-slate-500 shrink-0" />
                                )}
                            </button>
                            {isExpanded && entry.detail && (
                                <pre className="ml-8 mt-1 mb-2 p-2 bg-slate-900 border border-slate-700 rounded text-[10px] text-slate-300 overflow-x-auto whitespace-pre-wrap max-h-60 overflow-y-auto font-mono">
                                    {entry.detail}
                                </pre>
                            )}
                        </div>
                    );
                })}
            </div>
        </section>
    );
};

export default DiagnosticsPanel;
